import { z } from 'zod';
import { readFile } from '@/utils/fs';
import { ValidationError } from '@/types';

/**
 * Zod schema for validating essential parts of a Playwright JSON report
 */
const PlaywrightReportSchema = z.object({
  config: z.any(),
  suites: z.array(z.any()),
  stats: z.object({
    startTime: z.string(),
    duration: z.number(),
    expected: z.number(),
    skipped: z.number(),
    unexpected: z.number(),
    flaky: z.number(),
  }),
  errors: z.array(z.any()).optional(),
  metadata: z.any().optional(),
});

/**
 * TypeScript type for a validated Playwright report
 */
export type PlaywrightReport = z.infer<typeof PlaywrightReportSchema>;

/**
 * Read, parse, and validate a Playwright JSON report from disk.
 * @param jsonPath Absolute path to the Playwright JSON report
 * @throws ValidationError if reading, parsing, or validation fails
 */
export async function parsePlaywrightJson(
  jsonPath: string
): Promise<PlaywrightReport> {
  let raw: string;
  try {
    raw = await readFile(jsonPath);
  } catch (error) {
    throw new ValidationError(
      `Failed to read JSON report at "${jsonPath}": ${(error as Error).message}`
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw new ValidationError(
      `Invalid JSON format in report: ${(error as Error).message}`
    );
  }

  const result = PlaywrightReportSchema.safeParse(parsed);
  if (!result.success) {
    const issues = result.error.issues.map(
      issue => `${issue.path.join('.')}: ${issue.message}`
    );
    throw new ValidationError(
      `Playwright report validation failed: ${issues.join(', ')}`
    );
  }

  return result.data;
}
